import { useState, useEffect } from "react";
import { paymentComplementsApi, invoicesApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, FileCheck, Loader2, CheckCircle, MoreVertical, FileText, FileCode, Archive, Send } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAYMENT_FORMS = [
  { value: "01", label: "01 - Efectivo" },
  { value: "02", label: "02 - Cheque nominativo" },
  { value: "03", label: "03 - Transferencia electrónica" },
  { value: "04", label: "04 - Tarjeta de crédito" },
  { value: "28", label: "28 - Tarjeta de débito" },
];

const PaymentComplementsPage = () => {
  const [complements, setComplements] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    invoice_id: "",
    amount: "",
    payment_date: new Date().toISOString().split('T')[0],
    payment_form: "03"
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [complementsRes, invoicesRes] = await Promise.all([
        paymentComplementsApi.list(),
        invoicesApi.list()
      ]);
      setComplements(complementsRes.data);
      // Only show invoices that were created with PPD (partial payment)
      setInvoices(invoicesRes.data.filter(inv => inv.status === "issued" && inv.payment_method === "PPD"));
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.invoice_id || !formData.amount) {
      toast.error("Completa todos los campos");
      return;
    }

    setSubmitting(true);

    try {
      await paymentComplementsApi.create({
        ...formData,
        amount: parseFloat(formData.amount)
      });
      toast.success("Complemento de pago creado");
      setDialogOpen(false);
      setFormData({
        invoice_id: "",
        amount: "",
        payment_date: new Date().toISOString().split('T')[0],
        payment_form: "03"
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear complemento");
    } finally {
      setSubmitting(false);
    }
  };

  const handleIssue = async (complementId) => {
    try {
      await paymentComplementsApi.issue(complementId);
      toast.success("Complemento de pago timbrado");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al timbrar");
    }
  };

  const handleViewPdf = (complementId) => {
    window.open(`${API}/payment-complements/${complementId}/pdf`, "_blank");
  };

  const handleViewXml = (complementId) => {
    window.open(`${API}/payment-complements/${complementId}/xml`, "_blank");
  };

  const handleDownloadZip = async (complement) => {
    try {
      const token = localStorage.getItem("cia_token");
      const response = await axios.get(
        `${API}/payment-complements/${complement.id}/download-zip`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `complemento_pago_${complement.series}-${complement.folio}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Archivos descargados");
    } catch (error) {
      toast.error("Error al descargar archivos");
    }
  };

  const handleResend = async (complementId) => {
    try {
      const token = localStorage.getItem("cia_token");
      await axios.post(
        `${API}/payment-complements/${complementId}/resend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Complemento de pago reenviado al cliente");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al reenviar");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('es-MX');
    } catch {
      return '-';
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: "badge-draft",
      issued: "badge-issued"
    };
    const labels = {
      draft: "Borrador",
      issued: "Timbrado"
    };
    return <span className={badges[status]}>{labels[status]}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B9BD5]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payment-complements-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Complementos de Pago</h1>
          <p className="text-slate-600">Administra los complementos de pago (REP)</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="cia-btn-primary" data-testid="new-complement-btn">
              <Plus className="w-5 h-5 mr-2" />
              Nuevo Complemento
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Crear Complemento de Pago</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Factura Relacionada (PPD)</Label>
                <Select 
                  value={formData.invoice_id} 
                  onValueChange={(value) => setFormData({ ...formData, invoice_id: value })}
                >
                  <SelectTrigger data-testid="invoice-select">
                    <SelectValue placeholder="Seleccionar factura..." />
                  </SelectTrigger>
                  <SelectContent>
                    {invoices.length === 0 ? (
                      <SelectItem value="none" disabled>No hay facturas PPD disponibles</SelectItem>
                    ) : (
                      invoices.map((invoice) => (
                        <SelectItem key={invoice.id} value={invoice.id}>
                          {invoice.series}-{invoice.folio} - {invoice.client_name} - {formatCurrency(invoice.total)}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Monto del Pago</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    className="cia-input"
                    required
                    data-testid="amount-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fecha de Pago</Label>
                  <Input
                    type="date"
                    value={formData.payment_date}
                    onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                    className="cia-input"
                    required
                    data-testid="payment-date-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Forma de Pago</Label>
                <Select 
                  value={formData.payment_form} 
                  onValueChange={(value) => setFormData({ ...formData, payment_form: value })}
                >
                  <SelectTrigger data-testid="payment-form-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_FORMS.map((form) => (
                      <SelectItem key={form.value} value={form.value}>{form.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="cia-btn-primary" disabled={submitting} data-testid="create-complement-btn">
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Crear Complemento"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Complements table */}
      <Card className="cia-card">
        <CardContent className="p-0">
          {complements.length === 0 ? (
            <div className="text-center py-12">
              <FileCheck className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No hay complementos de pago</h3>
              <p className="text-slate-500 mb-6">
                Los complementos de pago se usan para facturas con método de pago PPD
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Folio</th>
                    <th>Factura</th>
                    <th>Monto</th>
                    <th>Fecha Pago</th>
                    <th>Forma Pago</th>
                    <th>Estado</th>
                    <th className="text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {complements.map((complement) => (
                    <tr key={complement.id} data-testid={`complement-row-${complement.id}`}>
                      <td className="font-medium">{complement.series}-{complement.folio}</td>
                      <td>{complement.invoice_id.slice(0, 8)}...</td>
                      <td>{formatCurrency(complement.amount)}</td>
                      <td>{formatDate(complement.payment_date)}</td>
                      <td>{complement.payment_form}</td>
                      <td>{getStatusBadge(complement.status)}</td>
                      <td className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            {complement.status === "draft" && (
                              <DropdownMenuItem onClick={() => handleIssue(complement.id)}>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Timbrar
                              </DropdownMenuItem>
                            )}
                            
                            {complement.status === "issued" && (
                              <>
                                <DropdownMenuItem onClick={() => handleViewPdf(complement.id)}>
                                  <FileText className="w-4 h-4 mr-2" />
                                  Ver PDF
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleViewXml(complement.id)}>
                                  <FileCode className="w-4 h-4 mr-2" />
                                  Ver XML
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDownloadZip(complement)}>
                                  <Archive className="w-4 h-4 mr-2" />
                                  Descargar ZIP
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleResend(complement.id)}>
                                  <Send className="w-4 h-4 mr-2" />
                                  Reenviar
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentComplementsPage;
